import { useState, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  Crown, 
  Buildings,
  ChartBar,
  SignOut,
  List,
  X,
  Globe,
  EnvelopeSimple,
  ClockCounterClockwise,
  Gear,
  Users,
  ShieldCheck,
  Icon
} from '@phosphor-icons/react';
import { Button } from './ui/button';

interface SuperAdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  icon: Icon;
  label: string;
  exact?: boolean;
}

export function SuperAdminLayout({ children }: SuperAdminLayoutProps) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'sv' ? 'en' : 'sv';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const navItems: NavItem[] = [
    { path: '/superadmin', icon: ChartBar, label: 'Översikt', exact: true },
    { path: '/superadmin/organizations', icon: Buildings, label: 'Föreningar' },
    { path: '/superadmin/users', icon: Users, label: 'Användare' },
    { path: '/superadmin/permissions', icon: ShieldCheck, label: 'Behörigheter' },
    { path: '/superadmin/emails', icon: EnvelopeSimple, label: 'E-post' },
    { path: '/superadmin/audit', icon: ClockCounterClockwise, label: 'Audit-logg' },
    { path: '/superadmin/settings', icon: Gear, label: 'Inställningar' },
  ];

  return (
    <div className="main-layout">
      {/* Desktop Sidebar */}
      <aside className="sidebar hidden md:flex flex-col bg-slate-900 text-white border-r-0">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-amber-500 flex items-center justify-center">
              <Crown size={18} className="text-white" weight="fill" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-white">
                Superadmin
              </h1>
              <p className="text-xs text-slate-400">Förvaltningen</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path) && location.pathname !== '/superadmin';
            const IconComponent = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive 
                    ? 'bg-slate-800 text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
              >
                <IconComponent size={20} weight={isActive ? 'fill' : 'regular'} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-slate-700 space-y-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-slate-300 hover:bg-slate-800 hover:text-white w-full text-sm transition-colors"
            data-testid="switch-to-admin"
          >
            <Buildings size={20} weight="regular" />
            <span>Gå till Admin-portal</span>
          </Link>
          
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-3 px-3 py-2 rounded-md text-slate-300 hover:bg-slate-800 hover:text-white w-full text-sm transition-colors"
            data-testid="language-toggle"
          >
            <Globe size={20} weight="regular" />
            <span>{i18n.language === 'sv' ? 'English' : 'Svenska'}</span>
          </button>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-md text-red-400 hover:bg-red-500/20 hover:text-red-300 w-full text-sm transition-colors"
            data-testid="logout-button"
          >
            <SignOut size={20} weight="regular" />
            <span>{t('logout')}</span>
          </button>
        </div>
        
        {user && (
          <div className="p-4 border-t border-slate-700">
            <div className="text-sm font-medium text-white truncate">{user.name}</div>
            <div className="text-xs text-slate-400 truncate">{user.email}</div>
          </div>
        )}
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 z-40">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <Crown size={20} className="text-amber-500" weight="fill" />
            <span className="text-base font-semibold text-white">Superadmin</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white hover:bg-slate-800"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={20} /> : <List size={20} />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-slate-900 z-30 pt-14">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-md text-slate-300 hover:bg-slate-800 hover:text-white text-base"
                >
                  <IconComponent size={22} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <hr className="my-4 border-slate-700" />
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-slate-300 w-full text-base"
            >
              <Globe size={22} />
              <span>{i18n.language === 'sv' ? 'English' : 'Svenska'}</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-red-400 w-full text-base"
            >
              <SignOut size={22} />
              <span>{t('logout')}</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content md:pt-0 pt-14 bg-slate-50">
        {children}
      </main>
    </div>
  );
}

export default SuperAdminLayout;
